import {
  DEFAULT_COUNTRY_CODE,
  getPostalCodeLabel,
  getPostalCodePattern,
  getProvinceLabel,
  isProvinceRequired,
  isUnitedStates,
} from '@/lib/constants/countries';
import { fetchShippingRulesForLocation } from '@/lib/functions/fetchShippingRulesForLocation';
import { normalizeStateCode } from '@/lib/functions/normalizeStateCode';
import { normalizeZipCode } from '@/lib/functions/normalizeZipCode';

import type { ProcessedShippingEligibility } from '@/lib/types/shippingEligibilityState';

const INELIGIBLE_MESSAGE =
  'We\u2019re sorry, your address is not available for shipping. Please try another address.';

export type ShippingEligibilityResult = {
  eligible: boolean;
  message: string;
  processedRules?: ProcessedShippingEligibility;
};

export async function checkShippingEligibilityForLocation(
  provinceCode: string,
  postalCode: string,
  countryCode: string = DEFAULT_COUNTRY_CODE,
): Promise<ShippingEligibilityResult> {
  const country = countryCode.toUpperCase();
  const provinceRequired = isProvinceRequired(country);
  const state = isUnitedStates(country) ? normalizeStateCode(provinceCode) : provinceCode.trim();
  const zipCode = isUnitedStates(country) ? normalizeZipCode(postalCode) : postalCode.trim();
  const postalPattern = getPostalCodePattern(country);
  const trimmedPostal = postalCode.trim();

  if (!postalPattern.test(trimmedPostal) || !zipCode) {
    return {
      eligible: false,
      message: `Please enter a valid ${getPostalCodeLabel(country).toLowerCase()}.`,
    };
  }

  if (provinceRequired && !state) {
    return {
      eligible: false,
      message: isUnitedStates(country)
        ? 'Please select a state.'
        : `Please select a ${getProvinceLabel(country).toLowerCase()}.`,
    };
  }

  try {
    const processedRules = await fetchShippingRulesForLocation(provinceCode, postalCode, country);

    if (!processedRules.availableMethods.length) {
      return {
        eligible: false,
        message: INELIGIBLE_MESSAGE,
      };
    }

    return { eligible: true, message: '', processedRules };
  } catch {
    return {
      eligible: false,
      message: 'Unable to verify shipping eligibility. Please try again.',
    };
  }
}
